import React, { useMemo, useRef, useState } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { AssignedRole } from '../services/authService';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAuth } from '../context/AuthContext';
import { useAppColors } from '../context/PreferencesContext';
import { isCompactWidth } from '../utils/responsiveLayout';
import { webPointer } from '../utils/webLayout';

type Anchor = { x: number; y: number; width: number; height: number };

function roleLabel(role: AssignedRole | undefined, fallback: string): string {
  const name = role?.roleName?.trim();
  return name || fallback;
}

/**
 * Active-role control for dashboard headers.
 * One assigned role is shown as a label. Several roles open a popover on wide
 * screens and a bottom sheet on phones.
 */
export function RoleSwitcher() {
  const auth = useAuth();
  const palette = useAppColors();
  const insets = useSafeAreaInsets();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const compact = isCompactWidth(windowWidth);
  const anchorRef = useRef<View>(null);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const [busyCode, setBusyCode] = useState<string | null>(null);
  const [error, setError] = useState('');

  const assignedRoles = auth.assignedRoles ?? [];
  const activeCode = auth.activeRole?.roleCode || auth.roles?.[0] || '';
  const active = assignedRoles.find(role => role.roleCode === activeCode);
  const label = roleLabel(active, activeCode);
  const canSwitch = assignedRoles.length > 1 && typeof auth.switchRole === 'function';

  const menuWidth = Math.min(280, windowWidth - 16);
  const menuStyle = useMemo(() => {
    if (!anchor || compact) return null;
    const rowHeight = 52;
    const estimatedHeight = Math.min(assignedRoles.length * rowHeight + 28, 320);
    let left = anchor.x + anchor.width - menuWidth;
    left = Math.max(8, Math.min(left, windowWidth - menuWidth - 8));
    let top = anchor.y + anchor.height + 6;
    if (top + estimatedHeight > windowHeight - 8) {
      top = Math.max(8, anchor.y - estimatedHeight - 6);
    }
    return { top, left, width: menuWidth };
  }, [anchor, assignedRoles.length, compact, menuWidth, windowHeight, windowWidth]);

  if (!label) return null;

  function close() {
    if (busyCode) return;
    setOpen(false);
    setError('');
  }

  function showMenu() {
    setError('');
    setOpen(true);
    const node = anchorRef.current;
    if (!node || typeof node.measureInWindow !== 'function') return;
    node.measureInWindow((x, y, width, height) => {
      setAnchor({ x, y, width, height });
    });
  }

  async function selectRole(role: AssignedRole) {
    if (busyCode || role.roleCode === activeCode) {
      setOpen(false);
      return;
    }
    setError('');
    setBusyCode(role.roleCode);
    try {
      await auth.switchRole(role.roleCode);
      setOpen(false);
    } catch (switchError) {
      const message =
        switchError &&
        typeof switchError === 'object' &&
        'message' in switchError &&
        typeof switchError.message === 'string'
          ? switchError.message
          : 'The role could not be switched. You are still using the previous role.';
      setError(message);
    } finally {
      setBusyCode(null);
    }
  }

  const chip = (
    <View
      ref={anchorRef}
      collapsable={false}
      style={[styles.chip, compact ? styles.chipCompact : null]}
    >
      <Text
        numberOfLines={1}
        style={[styles.chipText, { color: palette.white }]}
      >
        {label}
      </Text>
      {canSwitch ? (
        <Text style={[styles.chevron, { color: palette.white }]}>▾</Text>
      ) : null}
    </View>
  );

  const options = assignedRoles.map(role => {
    const selected = role.roleCode === activeCode;
    const busy = busyCode === role.roleCode;
    return (
      <Pressable
        key={role.roleCode}
        accessibilityRole="button"
        accessibilityState={{ selected, disabled: Boolean(busyCode) }}
        accessibilityLabel={
          selected ? `${role.roleName}, current role` : `Switch to ${role.roleName}`
        }
        disabled={Boolean(busyCode)}
        onPress={() => selectRole(role)}
        style={({ pressed }) => [
          styles.option,
          webPointer,
          {
            backgroundColor: pressed || selected ? `${palette.primary}12` : palette.surface,
          },
        ]}
      >
        <View style={styles.optionText}>
          <Text
            numberOfLines={2}
            style={[
              styles.optionName,
              selected ? styles.optionNameSelected : null,
              { color: palette.textPrimary },
            ]}
          >
            {role.roleName}
          </Text>
          {selected ? (
            <Text style={[styles.current, { color: palette.primary }]}>Current</Text>
          ) : null}
        </View>
        <Text style={[styles.mark, { color: selected ? palette.primary : palette.transparent }]}>
          {busy ? '…' : '✓'}
        </Text>
      </Pressable>
    );
  });

  return (
    <>
      {canSwitch ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Switch role. Current role ${label}`}
          accessibilityHint="Shows the roles assigned to you"
          accessibilityState={{ expanded: open }}
          onPress={showMenu}
          style={webPointer}
          testID="role-switcher"
        >
          {chip}
        </Pressable>
      ) : (
        <View
          accessibilityRole="text"
          accessibilityLabel={`Current role ${label}`}
          testID="role-switcher"
        >
          {chip}
        </View>
      )}
      <Modal
        visible={open}
        transparent
        animationType={compact ? 'slide' : 'fade'}
        onRequestClose={close}
      >
        <View style={[styles.backdrop, compact ? styles.backdropSheet : null]}>
          <Pressable
            accessibilityLabel="Close role list"
            onPress={close}
            style={styles.backdropFill}
          />
          <View
            style={[
              compact ? styles.sheet : styles.popover,
              compact
                ? { paddingBottom: Math.max(insets.bottom, spacing.lg) }
                : menuStyle,
              {
                backgroundColor: palette.surface,
                borderColor: palette.border,
              },
            ]}
          >
            <Text style={[styles.menuTitle, { color: palette.textSecondary }]}>
              Switch role
            </Text>
            {options}
            {error ? (
              <Text accessibilityRole="alert" style={[styles.error, { color: palette.error }]}>
                {error}
              </Text>
            ) : null}
          </View>
        </View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  chip: {
    minHeight: 36,
    maxWidth: 220,
    borderWidth: 1,
    borderRadius: 18,
    borderColor: 'rgba(255,255,255,0.45)',
    backgroundColor: 'rgba(255,255,255,0.16)',
    paddingHorizontal: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  chipCompact: { maxWidth: 148 },
  chipText: {
    ...typography.caption,
    fontWeight: '700',
    flexShrink: 1,
  },
  chevron: {
    fontSize: 12,
    lineHeight: 14,
  },
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.28)',
  },
  backdropFill: {
    ...StyleSheet.absoluteFillObject,
  },
  backdropSheet: {
    justifyContent: 'flex-end',
  },
  popover: {
    position: 'absolute',
    borderWidth: 1,
    borderRadius: 14,
    paddingVertical: spacing.sm,
    overflow: 'hidden',
  },
  sheet: {
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    borderTopWidth: 1,
    paddingTop: spacing.md,
  },
  menuTitle: {
    ...typography.caption,
    fontWeight: '700',
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.xs,
  },
  option: {
    minHeight: 48,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  optionText: { flex: 1, minWidth: 0 },
  optionName: { ...typography.body, fontWeight: '600' },
  optionNameSelected: { fontWeight: '700' },
  current: { ...typography.caption, fontWeight: '700' },
  mark: { fontSize: 16, fontWeight: '700', width: 18, textAlign: 'center' },
  error: {
    ...typography.caption,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
});
